import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Star, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const monthLabel = (m) => {
  if (!m) return "Bulan Berjalan";
  const [y, mm] = m.split("-");
  return `${BULAN[Number(mm) - 1]} ${y}`;
};

const BLN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const shortDate = (iso) => {
  if (!iso) return "";
  const t = new Date(iso);
  return `${t.getDate()} ${BLN[t.getMonth()]}`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "-");

// Dashboard: review Google Bisnis per profil. Mode Places (API key): review bulan sebelumnya (rekap akhir bulan 16:00 WIB
// / data awal) vs review sekarang (update otomatis tiap hari 17:00 WIB) — tanpa refresh manual.
const GoogleReviewCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard/google-reviews").then((r) => setD(r.data)).catch(() => setD({ connected: false, profiles: [] }));
  }, []);

  const rows = d?.profiles || [];
  const places = d?.mode === "places";
  const totalCount = rows.reduce((s, p) => s + (p.count || 0), 0);
  const totalStars = rows.reduce((s, p) => s + (p.star_sum || 0), 0);

  return (
    <div className="bg-white border border-[#E4E4E7] mb-6" data-testid="google-review-card">
      <div className="px-4 sm:px-6 py-3 border-b border-[#E4E4E7] flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Star size={14} className="text-[#F59E0B] fill-[#F59E0B]" />
          <span className="label-cap text-[10px] sm:text-xs">Google Bisnis Review{places ? "" : ` · ${monthLabel(d?.month)}`}</span>
        </div>
        <div className="flex items-center gap-3">
          {places && (
            <span className="text-xs font-mono font-bold">{rows.reduce((s, p) => s + (p.total || 0), 0)} review total</span>
          )}
          {!places && d?.connected && totalCount > 0 && (
            <span className="text-xs font-mono font-bold">{totalCount} review · {totalStars} ★</span>
          )}
        </div>
      </div>
      {d && !d.connected && (
        <div className="px-4 sm:px-6 py-3 text-xs text-[#52525B] border-b border-[#E4E4E7]">
          Belum ada sumber data — isi Places API key di <Link to="/settings" className="text-[#002FA7] font-bold hover:underline">Pengaturan → Google Bisnis</Link>.
        </div>
      )}
      {d?.error && (
        <div className="px-4 sm:px-6 py-2 text-[11px] text-[#B45309] bg-[#F59E0B]/10 border-b border-[#F59E0B]/30 break-words">{d.error}</div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E4E7]">
        {(rows.length ? rows : Array.from({ length: 4 }, (_, i) => ({ key: i, label: "…" }))).map((p) => (
          <div key={p.key} className="bg-white p-4" data-testid={`google-review-${p.key}`}>
            <div className="flex items-center justify-between gap-1 mb-2">
              <span className="text-xs font-bold truncate">{p.label}</span>
              {p.link && <a href={p.link} target="_blank" rel="noreferrer" title="Buka di Google Maps" className="text-[#52525B] hover:text-[#002FA7] shrink-0"><ExternalLink size={12} /></a>}
            </div>
            {places && !p.error && p.total != null ? (
              <>
                <div className="flex items-center justify-between gap-2 py-1 border-b border-[#F4F4F5]">
                  <span className="text-[11px] text-[#52525B]">
                    Review bulan sebelumnya
                    <span className="block text-[9px] text-[#A1A1AA]">{p.prev_total == null ? "belum ada" : p.prev_initial ? `data awal ${shortDate(p.prev_saved_at)}` : `s/d akhir ${monthLabel(p.prev_month)}`}</span>
                  </span>
                  <span className="font-mono text-lg font-bold">{p.prev_total ?? "-"}</span>
                </div>
                <div className="flex items-center justify-between gap-2 py-1">
                  <span className="text-[11px] text-[#52525B]">
                    Review sekarang
                    {p.month_new != null && <span className={`block text-[9px] font-bold ${p.month_new > 0 ? "text-[#059669]" : "text-[#A1A1AA]"}`}>{p.month_new > 0 ? `+${p.month_new}` : p.month_new} bulan ini</span>}
                  </span>
                  <span className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">{p.total}</span>
                </div>
                <div className="text-[10px] text-[#52525B] mt-1">
                  <span className="text-[#F59E0B]">★</span> {p.rating != null ? Number(p.rating).toFixed(1) : "-"} rating
                </div>
              </>
            ) : !d?.connected || p.error || p.count == null ? (
              <div className="text-[11px] text-[#A1A1AA]">{p.error && d?.connected ? "Gagal / tidak ditemukan" : "—"}</div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">{p.count}</span>
                  <span className="text-[10px] text-[#52525B]">review baru</span>
                </div>
                <div className="text-xs text-[#52525B] mb-2">
                  <b className="text-[#111113]">{p.star_sum} ★</b>{p.avg_month ? ` · rata-rata ${p.avg_month}` : ""}
                </div>
                <div className="space-y-0.5">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const n = p.stars?.[String(s)] || 0;
                    const w = p.count ? Math.round((n / p.count) * 100) : 0;
                    return (
                      <div key={s} className="flex items-center gap-1.5 text-[10px]">
                        <span className="w-5 font-mono text-[#52525B]">{s}★</span>
                        <div className="flex-1 h-1.5 bg-[#F4F4F5]"><div className={`h-1.5 ${s >= 4 ? "bg-[#F59E0B]" : "bg-[#DC2626]"}`} style={{ width: `${w}%` }} /></div>
                        <span className="w-5 text-right font-mono">{n}</span>
                      </div>
                    );
                  })}
                </div>
                {p.avg_all != null && <div className="text-[10px] text-[#A1A1AA] mt-2">Total profil: {p.total_all} review · ★ {Number(p.avg_all).toFixed(1)}</div>}
              </>
            )}
          </div>
        ))}
      </div>
      {places && (
        <div className="px-4 sm:px-6 py-2 border-t border-[#E4E4E7] text-[10px] text-[#A1A1AA]" data-testid="google-review-footer">
          Update otomatis tiap hari {d.schedule?.daily || "17:00"} WIB · rekap bulan disimpan tanggal akhir bulan {d.schedule?.close || "16:00"} WIB · terakhir diperbarui {fmtTime(d.fetched_at)}
        </div>
      )}
    </div>
  );
};

export default GoogleReviewCard;
